import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useBasicDetails } from '../../store';
import { getTemplateLayout } from '../../common/utils/FallbackCondition';
import { AssessmentPageLayout01 } from './layout_01/AssessmentPageLayout';
import { AssessmentPageLayout02 } from './layout_02/AssessmentPageLayout';
import { IFrameStyles } from '../styles';
import {
  EXPERIENCE_DOMAIN_CONFIG,
  EXPERIENCE_HOSTNAMES,
  routeMappings,
  SessionStore,
  UP_HOST_DOMAINS,
} from '../../constants';
import { AM_VIDEO_INTERVIEW_PACKAGE_TAG } from '../constants/constants';
import { validateAssessment, validateAssessmentLaunchUrl } from '../../common/utils/validateAssessmentLaunchUrl';
import { getCookie } from '../../common/utils/cookies';
import { useHistory } from 'react-router-dom';
import { logoutUser } from '../../common/utils/logoutUser';
import { sendPageViewDetails } from '../../common/utils/gaEvents';
import { GAPageViewMappings, GARouteMappings } from '../../constants/gaMappings';
import { RoutePageProps } from '../../base/components/Routes';
import { ValidateAssessmentLaunchConfig, ValidateAssessmentPayload } from '../interfaces';
import { TaskItem } from '../../survey/interfaces/SurveyTaskTile';
import { getExperienceInitCookie } from '../../common/utils/experienceInitCookie';

interface AssessmentPageProps extends RoutePageProps {
  getNextWorkflowItem?(): Promise<void>;
}
export interface AssessmentPageLayoutProps {
  loading: boolean;
  parsedLaunchAssessmentConfig: ValidateAssessmentLaunchConfig;
  launchurl: string;
}
const createAssessmentLaunchUrl = (launchConfig: ValidateAssessmentLaunchConfig): string => {
  return `/player/testdriver/start?instructionShown=false&isPractice=false&isWarmup=false&componentId=${
    launchConfig.activeComponentId
  }&projectId=${launchConfig.projectId}&lang=${launchConfig.selectedLanguageCode || 'EN_US'}&sittingId=${
    launchConfig.sittingId
  }&packageId=${launchConfig.packageId}&originalCPId=${launchConfig.originalPackageId || launchConfig.packageId}`;
  // &oldAssessmentId=1312 --- need to discuss
};

interface AssessmentPageProps extends RoutePageProps {
  currentWorkflowItem?: TaskItem;
  getNextWorkflowItem?(): Promise<void>;
}

let isValidatingAssessment = false;

/** Component for playing Assessment in Iframe */
export const AssessmentPage = (props: AssessmentPageProps): JSX.Element => {
  const {
    template: { layoutCode },
  } = useBasicDetails();

  const layout = getTemplateLayout(layoutCode);

  /** page view ga */
  useEffect(() => {
    sendPageViewDetails(GARouteMappings.ASSESSMENT, GAPageViewMappings.ASSESSMENT);
  }, []);

  const { currentWorkflowItem, getNextWorkflowItem } = props;
  const classes = IFrameStyles();
  const history = useHistory();
  const [launchurl, setLaunchurl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const tcPlusFlow = getCookie('TcPlusFlag') === '1';

  const launchAssessmentConfig = sessionStorage.getItem(SessionStore.launchconfig);
  const parsedLaunchAssessmentConfig = launchAssessmentConfig && JSON.parse(launchAssessmentConfig);

  useLayoutEffect(() => {
    const html = document.getElementsByTagName('html')[0];
    html.setAttribute('style', 'overflow:hidden');
    const body = document.getElementsByTagName('body')[0];
    const initialClass = body.className;
    body.className = `notranslate ${classes.container}`;

    return (): void => {
      html.removeAttribute('style');
      body.className = initialClass;
    };
  }, [classes.container]);

  // Hide hotjar widget on assessment page
  useLayoutEffect(() => {
    const hotjarWidget = document.getElementsByClassName('_hj-widget-container')[0] as HTMLDivElement;

    if (hotjarWidget) {
      hotjarWidget.style.display = 'none';
    }
  }, []);

  useEffect(() => {
    if (tcPlusFlow && !isValidatingAssessment) {
      const expCookie = getExperienceInitCookie();
      if (
        currentWorkflowItem &&
        currentWorkflowItem.moduleIds &&
        currentWorkflowItem.amcatId &&
        currentWorkflowItem.itemId &&
        currentWorkflowItem.state &&
        expCookie &&
        expCookie.companyUserId
      ) {
        const validateAssessmentPayload: ValidateAssessmentPayload = {
          companyUserId: expCookie.companyUserId,
          returnUrl: `${window.location.origin}/experience/#/task/${parsedLaunchAssessmentConfig.taskId}/assessment/${parsedLaunchAssessmentConfig.itemId}/evaluating`,
          moduleIDs: currentWorkflowItem.moduleIds,
          amcatId: currentWorkflowItem.amcatId,
          itemId: currentWorkflowItem.itemId,
          state: currentWorkflowItem.state,
        };

        validateAssessment(validateAssessmentPayload).then((res) => {
          isValidatingAssessment = true;
          setLaunchurl(res.data.token);
          setLoading(false);
        });
      }
    }
  }, [tcPlusFlow, currentWorkflowItem, parsedLaunchAssessmentConfig]);

  useEffect(() => {
    if (!tcPlusFlow && parsedLaunchAssessmentConfig) {
      // Pass navigation language for package with tag AM_VIDEO_INTERVIEW
      if (parsedLaunchAssessmentConfig.packageTag?.toUpperCase() === AM_VIDEO_INTERVIEW_PACKAGE_TAG)
        parsedLaunchAssessmentConfig.selectedLanguageCode = getCookie('locale_player') || 'EN_US';

      validateAssessmentLaunchUrl(parsedLaunchAssessmentConfig).then((res) => {
        if (!res.error) {
          let url = createAssessmentLaunchUrl(parsedLaunchAssessmentConfig);
          const launchUrl = sessionStorage.getItem(SessionStore.launchurl);
          if (launchUrl && performance.navigation.type === performance.navigation.TYPE_RELOAD) {
            url = launchUrl;
          }
          setLaunchurl(url);
          setLoading(false);
        } else if (res.error) {
          logoutUser();
          history.push(routeMappings['EXPIRED_LINK']);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // list of origins from where you wish to read the messages
    const trustedOrigins = [process.env.REACT_APP_PLAYER_API_DOMAIN || window.location.origin];
    const handleAssessmentExitMessage = (message: MessageEvent): void => {
      if (
        trustedOrigins.includes(message.origin) &&
        typeof message.data === 'string' &&
        JSON.parse(message.data).message === 'EXPERIENCE_END'
      ) {
        sessionStorage.removeItem(SessionStore.launchurl);
        sessionStorage.removeItem(SessionStore.launchconfig);
        if (getNextWorkflowItem) getNextWorkflowItem();
      }
    };
    window.addEventListener('message', handleAssessmentExitMessage, false);

    // remove the event listener as the page is unmounted
    return (): void => {
      window.removeEventListener('message', handleAssessmentExitMessage, false);
    };
  }, [getNextWorkflowItem]);

  const AssessmentPageLayoutMap: { [key: string]: React.FC<AssessmentPageLayoutProps> } = {
    TEMPLATE_LAYOUT_01: AssessmentPageLayout01,
    TEMPLATE_LAYOUT_02: AssessmentPageLayout02,
  };

  if (tcPlusFlow && launchurl) {
    const hostname = window.location.hostname as EXPERIENCE_DOMAIN_CONFIG;
    const environment = EXPERIENCE_HOSTNAMES[hostname];
    window.location.href = `${UP_HOST_DOMAINS[environment]}?autoLoginVersion=3&token=${launchurl}`;
    return <></>;
  }

  return (
    <>
      {AssessmentPageLayoutMap[layout] &&
        React.createElement(AssessmentPageLayoutMap[layout], {
          loading,
          parsedLaunchAssessmentConfig,
          launchurl,
        })}
    </>
  );
};
